import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getListing, updateListing, uploadImage } from '../lib/supabase';

const CATEGORIES = [
  'Clothing & Accessories',
  'Household',
  'Furniture',
  'Toys & Games',
  'Tools & Garden',
  'Electronics & Media',
  'Kitchenware',
  'Sports Equipment',
  'Miscellaneous',
];

export default function EditListing() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ title: '', price: '', available_date: '', category: '' });
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [currentImage, setCurrentImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    load();
  }, [id]); // eslint-disable-line

  const load = async () => {
    try {
      const data = await getListing(id);
      if (data.user_id !== user.id) { navigate('/dashboard'); return; }
      setForm({
        title: data.title || '',
        price: data.price || '',
        available_date: data.available_date || '',
        category: data.category || '',
      });
      setCurrentImage(data.image_url || null);
    } catch (e) {
      setError('Could not load listing.');
    } finally {
      setLoading(false);
    }
  };

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('Image must be under 5 MB.'); return; }
    setError('');
    setImageFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    setImageFile(null);
    setPreview(null);
    setCurrentImage(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      let image_url = currentImage;
      if (imageFile) image_url = await uploadImage(imageFile, user.id);

      await updateListing(id, {
        title: form.title.trim(),
        price: form.price === `` ? 0 : parseFloat(form.price),
        available_date: form.available_date || null,
        category: form.category || null,
        image_url,
      });
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="spinner-wrap"><div className="spinner" /></div>;

  const displayImage = preview || currentImage;

  return (
    <div className="page">
      <button className="btn btn-g btn-sm" onClick={() => navigate('/dashboard')} style={{ marginBottom: '1.5rem' }}>
        ← Back to Dashboard
      </button>

      <div className="form-wrap">
        <div className="form-card">
          <div className="form-head">
            <h2>Edit Listing</h2>
            <p>Update your item details below.</p>
          </div>

          {error && <div className="alert alert-e">{error}</div>}

          <form onSubmit={submit}>
            <div className="fg">
              <label>Title and Description *</label>
              <input value={form.title} onChange={set('title')} placeholder="What are you selling?" required />
            </div>

            <div className="fg">
              <label>Price (USD) *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.price}
                onChange={set('price')}
                placeholder="0.00"
                required
              />
            </div>

            <div className="fg">
              <label>Photo</label>
              <div
                className={`upload-area ${displayImage ? 'has' : ''}`}
                onClick={() => document.getElementById('edit-img-input').click()}
              >
                {displayImage ? (
                  <img className="upload-preview" src={displayImage} alt="Preview" />
                ) : (
                  <div>
                    <div style={{ fontSize: '2rem', marginBottom: '.5rem' }}>📷</div>
                    <div style={{ fontSize: '.875rem', color: 'var(--brown)' }}>Click to upload a photo</div>
                  </div>
                )}
              </div>
              <input id="edit-img-input" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImage} />
              {displayImage && (
                <button type="button" className="btn btn-g btn-sm" style={{ marginTop: '.5rem' }} onClick={removeImage}>
                  Remove photo
                </button>
              )}
            </div>

            <div className="fg">
              <label>Category <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
              <select value={form.category} onChange={set('category')} style={{ width: '100%' }}>
                <option value="">— Select a category —</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div className="fg">
              <label>Available Date <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
              <input type="date" value={form.available_date} onChange={set('available_date')} />
              <div className="hint">Leave blank if available immediately.</div>
            </div>

            <div style={{ display: 'flex', gap: '.75rem', marginTop: '.5rem' }}>
              <button className="btn btn-g" type="button" onClick={() => navigate('/dashboard')}>Cancel</button>
              <button className="btn btn-p" type="submit" disabled={saving} style={{ flex: 1 }}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
